import { RelatedKeywordTip } from '@/@types/search'
import MenuItem from '@/components/DropdownMenu/Item'
import { RELATED_KEYWORD_TIP } from '@/constants/api'
import { getKeywordTip } from '@/utils/fetchers/search'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import parseJikeURLScheme, { JikeTopicURLScheme } from '@/utils/jikeURLScheme'
import queryClient from '@/utils/queryClient'
import { css } from '@emotion/react'
import { Autocomplete, AutocompleteProps } from '@mantine/core'
import { SearchOutlineIcon } from '@ruguoapp/jike-fe-icons'
import { useQuery } from '@tanstack/react-query'
import debounce from 'debounce-promise'
import { isEmpty } from 'lodash-es'
import { AnimatePresence } from 'motion/react'
import { useState } from 'react'
import { MenuItemData } from '../DropdownMenu/types'

interface Tip extends MenuItemData, Pick<RelatedKeywordTip, 'type'> {}

const debouncedGetKeywordTip = debounce(getKeywordTip, 500)

export interface SearchBarProps {
	defaultValue?: string
}
export default function SearchBar({ defaultValue }: SearchBarProps) {
	const [isCompositing, setIsCompositing] = useState(false)
	const [isFocused, setIsFocused] = useState(false)
	const [keywords, setKeywords] = useState<string>(defaultValue || '')

	const keywordsQueryOptionsKey = [RELATED_KEYWORD_TIP, keywords]
	const { data = [] } = useQuery({
		queryKey: keywordsQueryOptionsKey,
		queryFn: () => {
			if (isEmpty(keywords)) {
				return []
			}
			return debouncedGetKeywordTip(keywords!).then(
				(data) =>
					data.map((item) => ({
						id: item.url,
						iconURL: item.icon,
						title: item.suggestion,
						type: item.type,
					})) as Tip[]
			)
		},
		enabled: !isEmpty(keywords) && !isCompositing,
	})
	const navigate = useUniversalNavigate()

	const renderOption: AutocompleteProps['renderOption'] = ({ option, checked }) => {
		const index = data.findIndex((item) => item.title === option.value)

		if (index === -1) {
			return null
		}
		const itemData = data[index]
		return (
			<AnimatePresence>
				<MenuItem data={itemData} isActive={checked} />
			</AnimatePresence>
		)
	}
	const handlePostSubmit = () => {
		queryClient.setQueryData(keywordsQueryOptionsKey, [])
	}
	const handleOptionSubmit: AutocompleteProps['onOptionSubmit'] = (value) => {
		const index = data.findIndex((item) => item.title === value)
		const itemData = data[index]

		switch (itemData.type) {
			case 'topic':
				const url = itemData.id
				const { type, id } = parseJikeURLScheme(url) as JikeTopicURLScheme
				if (type !== 'TOPIC') {
					return
				}
				navigate({
					to: '/topic/$id',
					params: {
						id,
					},
				})

				break
			case 'keyword':
				navigate({
					to: '/search',
					search: {
						q: itemData.title,
					},
				})
				break
		}
		handlePostSubmit()
	}
	const tipsData = data
		.filter((item) => ['topic', 'keyword'].includes(item.type))
		.map((item) => ({ ...item, value: item.title }))
	const noTipsData = isEmpty(tipsData)

	return (
		<div
			css={css`
				position: relative;

				[data-combobox-selected='true'] {
					> [role='menuitem'] {
						background: var(--selected-on-body-1);
					}
				}
			`}
		>
			<Autocomplete
				value={keywords}
				data={tipsData}
				dropdownOpened={isFocused && !noTipsData}
				renderOption={renderOption}
				onCompositionStart={() => {
					setIsCompositing(true)
				}}
				onCompositionEnd={() => {
					setIsCompositing(false)
				}}
				onChange={setKeywords}
				onOptionSubmit={handleOptionSubmit}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && keywords) {
						navigate({
							to: '/search',
							search: {
								q: keywords,
							},
						})
						handlePostSubmit()
					}
				}}
				onFocus={() => void setIsFocused(true)}
				onBlur={() => void setIsFocused(false)}
				withScrollArea={false}
				placeholder="搜索"
				clearable
				leftSection={
					<SearchOutlineIcon
						css={css`
							width: 24px;
							height: 24px;
						`}
					/>
				}
				clearButtonProps={{
					className: 'clear-button',
				}}
				classNames={{
					dropdown: 'search-tips-dropdown',
				}}
				styles={{
					dropdown: {
						position: 'absolute',
						left: 0,
						padding: '4px',
						borderRadius: '10px',
						background: 'var(--bg-on-body-1, #fff)',
						fontSize: '14px',
						lineHeight: '20px',
						boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.24)',
						width: '100%',
						maxWidth: 'var(--page-container-width)',
						// display: noTipsData ? 'none' : 'block',
					},
				}}
				comboboxProps={{
					withinPortal: false,
					width: '100%',
					offset: 16,
				}}
				css={css`
					> [data-variant] {
						border: 1px solid transparent;
						border-radius: 24px;
						padding: 8px 16px;
						position: relative;
						display: flex;
						align-items: center;
						gap: 10px;
						color: var(--tint-tertiary);
						background-color: var(--bg-on-body-2);
						transition:
							background-color 0.2s,
							border-color 0.2s;

						> svg {
							transition: color 0.2s;
						}

						&:focus-within,
						&:focus,
						&:active {
							background-color: var(--bg-on-body-1);
							border-color: var(--tint-separator);
							color: var(--tint-jike-blue);
						}

						.clear-button {
							appearance: none;
							border: none;
							outline: none;
							background: none;
							margin: 0;
							cursor: pointer;

							padding: 0;
							width: 24px;
							height: 24px;
							display: flex;
							justify-content: center;
							align-items: center;
							border-radius: 50%;

							transition: all 0.25s;
						}

						input {
							appearance: none;
							border: none;
							outline: none;
							background: none;
							padding: unset;
							margin: unset;

							width: 100%;
							height: 24px;
							font-size: 14px;
							color: var(--tint-primary);

							&:focus-within {
								& + div .clear-button {
									background-color: var(--bg-jike-blue);
									color: white;
								}
							}
						}
					}
				`}
			/>
		</div>
	)
}
